import { PAYLOAD_SCHEMA } from './constants';
import type { ExportPayload, SnapshotEntry } from './types';
import { endpointsFor, type ResolvedEndpoint } from './pageEndpoints';

const FORBIDDEN_KEYS = new Set([
  'tenant_id', 'tenantid', 'token', 'access_token', 'refresh_token',
  'authorization', 'password', 'secret', 'api_key',
]);

/** Entfernt tief alle Schlüssel, die Mandant oder Zugangsdaten tragen (nur Schlüssel, keine Werte-Heuristik). */
export function sanitizePayloadValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sanitizePayloadValue);
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (FORBIDDEN_KEYS.has(k.toLowerCase())) continue;
      out[k] = sanitizePayloadValue(v);
    }
    return out;
  }
  return value;
}

export type GetFn = (path: string, query?: Record<string, string>) => Promise<unknown>;

export interface CollectOptions {
  projectId: string;
  pages: string[];
  get: GetFn;
  onProgress?: (done: number, total: number) => void;
}

const CONCURRENCY = 6;

function listOf(response: unknown): Record<string, unknown>[] {
  if (Array.isArray(response)) return response as Record<string, unknown>[];
  const data = (response as { data?: unknown } | null)?.data;
  if (Array.isArray(data)) return data as Record<string, unknown>[];
  const items = (data as { items?: unknown } | null)?.items;
  return Array.isArray(items) ? (items as Record<string, unknown>[]) : [];
}

const label = (path: string, query?: Record<string, string>) =>
  query && Object.keys(query).length ? `${path}?${new URLSearchParams(query).toString()}` : path;

export async function collectSnapshot(opts: CollectOptions): Promise<{ entries: SnapshotEntry[]; missing: string[] }> {
  const { projectId, pages, get, onProgress } = opts;
  const entries: SnapshotEntry[] = [];
  const missing: string[] = [];
  const seen = new Set<string>();
  let done = 0;
  let total = 0;

  type Job = { path: string; query?: Record<string, string>; each?: ResolvedEndpoint['each'] };

  async function run(jobs: Job[]): Promise<Job[]> {
    const fresh = jobs.filter((j) => {
      const key = j.path + JSON.stringify(j.query ?? {});
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    total += fresh.length;
    const next: Job[] = [];
    for (let i = 0; i < fresh.length; i += CONCURRENCY) {
      const chunk = fresh.slice(i, i + CONCURRENCY);
      const results = await Promise.all(
        chunk.map(async (job) => {
          try {
            const response = sanitizePayloadValue(await get(job.path, job.query));
            return { job, response, ok: true as const };
          } catch {
            return { job, ok: false as const };
          }
        }),
      );
      for (const r of results) {
        done += 1;
        onProgress?.(done, total);
        if (!r.ok) {
          missing.push(label(r.job.path, r.job.query));
          continue;
        }
        entries.push({ path: r.job.path, ...(r.job.query ? { query: r.job.query } : {}), response: r.response });
        for (const e of r.job.each ?? []) {
          for (const item of listOf(r.response)) {
            const id = item[e.idField];
            if (typeof id === 'string' || typeof id === 'number') next.push({ path: e.child(String(id)) });
          }
        }
      }
    }
    return next;
  }

  let jobs: Job[] = endpointsFor(pages, projectId);
  while (jobs.length) jobs = await run(jobs);

  // Synthetische Projektliste für ProjectSwitcher/useProjects.
  const projectEntry = entries.find((e) => e.path === `/api/v1/projects/${projectId}`);
  if (projectEntry) {
    const r = projectEntry.response as { data?: unknown } | null;
    const project = r && typeof r === 'object' && r.data && typeof r.data === 'object' && !Array.isArray(r.data) ? r.data : r;
    entries.push({
      path: '/api/v1/projects',
      response: { success: true, data: [project], pagination: { cursor: null, hasMore: false } },
    });
  }
  return { entries, missing };
}

export function buildPayload(a: {
  projectId: string;
  projectName: string;
  pages: string[];
  entries: SnapshotEntry[];
  version: string;
}): ExportPayload {
  return {
    schema: PAYLOAD_SCHEMA,
    version: a.version,
    exportedAt: new Date().toISOString(),
    project: { id: a.projectId, name: a.projectName },
    pages: a.pages,
    entries: a.entries,
  };
}
